import { type JSX, Show } from 'solid-js';

// The dashboard's one on/off control (#5625, spec §3.9): a real
// `<button type="button" role="switch" aria-checked>`, so Space and Enter are
// native button activation and the state is one attribute a screen reader reads.
//
// CONTROLLED, never self-flipping: a click asks the caller to act
// (`onActivate`) and `aria-checked` only ever follows `checked`. The paint comes
// from the caller's re-read of the thing it toggles, not from the click's guess
// (`lib/agent-mode-save.ts`'s no-optimistic-paint rule), so a refused write
// leaves the switch where the server says it is.
//
// The button holds a track and a thumb and no text. The accessible name is the
// caller's (`aria-label`, or `aria-labelledby` when a visible label already
// names it); `stateLabel` is the visible On / Off beside it.

export type SwitchProps = {
  readonly checked: boolean;
  readonly disabled?: boolean;
  /** The click. The Switch never flips its own state; repaint from the re-read. */
  readonly onActivate: () => void;
  /** The visible state text beside the button ("On" / "Off"). */
  readonly stateLabel?: string;
  readonly 'aria-label'?: string;
  readonly 'aria-labelledby'?: string;
};

export function Switch(props: SwitchProps): JSX.Element {
  return (
    <span class="switch-field">
      <button
        type="button"
        role="switch"
        class="switch"
        aria-checked={props.checked}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        disabled={props.disabled === true}
        onClick={() => props.onActivate()}
      >
        <span class="switch__track" />
        <span class="switch__thumb" />
      </button>
      <Show when={props.stateLabel}>{(label) => <span class="switch__state">{label()}</span>}</Show>
    </span>
  );
}
